/**
 * Pure data shaping for the custom (PowerPoint-style) report: date and number formatting, the
 * summary/day/hour/group rows the slides chart, and the metric cards. Extracted from
 * customReportData.tsx so it can be tested without the data hook.
 */
import { EsPCAReportField } from 'c-sdk';
import type {
    ChartSpec,
    CustomGroupByField,
    CustomReportSlideConfig,
    InsightSpec,
} from './customReportTypes';
import type { MultiplierRow, SummaryBarRow, SummaryMetricCard } from './customReportData';

const nf = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });
const DOW_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const formatDate = (dateString: string, locale: 'GB' | 'US') => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    const year = date.getUTCFullYear();
    return locale === 'GB' ? `${day}/${month}/${year}` : `${month}/${day}/${year}`;
};

export const reformatDateForFilteredDates = (dateStr: string, locale: 'GB' | 'US') => {
    const ddmmyyyyRegex = /^\d{2}-\d{2}-\d{4}$/;
    if (ddmmyyyyRegex.test(dateStr)) {
        const [dd, mm, yyyy] = dateStr.split('-');
        return locale === 'GB' ? `${dd}/${mm}/${yyyy}` : `${mm}/${dd}/${yyyy}`;
    }
    const [yyyy, mm, dd] = dateStr.split('-');
    if (!yyyy || !mm || !dd) return '';
    return locale === 'GB' ? `${dd}/${mm}/${yyyy}` : `${mm}/${dd}/${yyyy}`;
};

export const fmtInt = (n?: number) => (typeof n === 'number' ? nf.format(Math.round(n)) : '0');

const num = (n: any) => Number(n ?? 0) || 0;

export const normalizeMultiplierRows = (input: any): MultiplierRow[] => {
    const raw = input?.data?.data ?? input?.data ?? input ?? [];
    if (!Array.isArray(raw)) return [];

    return raw.map((r: any) => ({
        display_owner: String(r.display_owner ?? r.owner ?? ''),
        display_format: String(r.display_format ?? r.format ?? ''),
        sum_of_play: num(r.sum_of_play ?? r.plays),
        dsp_impressions: num(r.dsp_impressions),
        geopath_impressions: num(r.geopath_impressions),
        variance: num(r.variance ?? r.im_variance),
        dsp_avg_impressions: num(r.dsp_avg_impressions),
        geopath_avg_impressions: num(r.geopath_avg_impressions),
        average_im: num(r.average_im),
        im_variance: num(r.im_variance),
        variance_percent: Number(r.variance_percent ?? 0),
    }));
};

export const fmtCompact = (n?: number) => {
    if (typeof n !== 'number' || !Number.isFinite(n)) return '0';
    const sign = n < 0 ? '-' : '';
    const v = Math.abs(n);
    if (v >= 1e9) return `${sign}${+(v / 1e9).toFixed(v >= 1e11 ? 0 : 1)}b`;
    if (v >= 1e6) return `${sign}${+(v / 1e6).toFixed(v >= 1e7 ? 0 : 1)}m`;
    if (v >= 1e3) return `${sign}${+(v / 1e3).toFixed(v >= 1e4 ? 0 : 1)}k`;
    return `${sign}${nf.format(Math.round(v))}`;
};

export const perfColor = (p: number | null) =>
    p == null ? 'text.primary' : p >= 100 ? '#158f43' : '#e11d48';

export const formatPercent = (p: number | null) => (p == null ? '-' : `${p.toFixed(1)}%`);

export const getBucket = (raw: any, iso: string) => raw?.[iso]?.[iso] ?? raw?.[iso] ?? {};

export const cumulative = (vals: number[]) => {
    let total = 0;
    return vals.map(v => {
        total += v || 0;
        return total;
    });
};

const hasAnyMetrics = (row: SummaryBarRow) =>
    row.impacts_total !== 0 ||
    row.impacts_expected !== 0 ||
    row.plays_total !== 0 ||
    row.plays_expected !== 0;

export const buildSummaryRows = (raw: Record<string, any>) =>
    Object.entries(raw)
        .map(([name, v]) => ({
            name,
            impacts_total: v?.impacts ?? 0,
            impacts_expected: v?.expected_impacts_in_schedule ?? 0,
            plays_total: v?.sum_of_plays ?? 0,
            plays_expected: v?.expected_plays_in_schedule ?? 0,
        }))
        .filter(hasAnyMetrics);

export const sortByMagnitude = (rows: SummaryBarRow[]) =>
    [...rows].sort((a, b) => {
        const aScore = Math.max(
            a.impacts_total,
            a.plays_total,
            a.impacts_expected,
            a.plays_expected,
        );
        const bScore = Math.max(
            b.impacts_total,
            b.plays_total,
            b.impacts_expected,
            b.plays_expected,
        );
        return bScore - aScore;
    });

export const buildEnvFormatRows = (raw: Record<string, any>) =>
    sortByMagnitude(
        Object.entries(raw).flatMap(([envName, nested]) => {
            if (!nested || typeof nested !== 'object') return [];
            return Object.entries(nested as Record<string, any>)
                .map(([formatName, v]) => ({
                    name: `${envName} - ${formatName}`,
                    impacts_total: v?.impacts ?? 0,
                    impacts_expected: v?.expected_impacts_in_schedule ?? 0,
                    plays_total: v?.sum_of_plays ?? 0,
                    plays_expected: v?.expected_plays_in_schedule ?? 0,
                }))
                .filter(hasAnyMetrics);
        }),
    );

export const GROUP_BY_FIELD_TO_ES_FIELD: Record<CustomGroupByField, EsPCAReportField> = {
    display_owner: EsPCAReportField.DisplayOwner,
    display_env: EsPCAReportField.DisplayEnv,
    display_format: EsPCAReportField.DisplayFormat,
    display_tv_region: EsPCAReportField.DisplayTvRegion,
    display_conurbation: EsPCAReportField.DisplayConurbation,
    day_of_week: EsPCAReportField.Day,
    hour_of_day: EsPCAReportField.Hour,
};

const MULTI_GROUP_BY_SUPPORTED_FIELDS: CustomGroupByField[] = [
    'display_owner',
    'display_env',
    'display_format',
    'display_tv_region',
    'display_conurbation',
];

const isMetricBucket = (value: any) =>
    value &&
    typeof value === 'object' &&
    ('impacts' in value ||
        'expected_impacts_in_schedule' in value ||
        'sum_of_plays' in value ||
        'expected_plays_in_schedule' in value);

export const buildMultiGroupRows = (
    raw: Record<string, any>,
    path: string[] = [],
): SummaryBarRow[] =>
    Object.entries(raw).flatMap(([key, value]) => {
        const nextPath = [...path, key];

        if (isMetricBucket(value)) {
            return [
                {
                    name: nextPath.join(' - '),
                    impacts_total: value?.impacts ?? 0,
                    impacts_expected: value?.expected_impacts_in_schedule ?? 0,
                    plays_total: value?.sum_of_plays ?? 0,
                    plays_expected: value?.expected_plays_in_schedule ?? 0,
                },
            ].filter(hasAnyMetrics);
        }

        if (!value || typeof value !== 'object') return [];
        return buildMultiGroupRows(value as Record<string, any>, nextPath);
    });

export const getChartGroupByFields = (chart: ChartSpec): CustomGroupByField[] => {
    if (chart.groupByFields?.length) return chart.groupByFields;
    if (chart.dataset === 'display_env_format') return ['display_env', 'display_format'];
    if (
        chart.dataset === 'display_owner' ||
        chart.dataset === 'display_env' ||
        chart.dataset === 'display_format' ||
        chart.dataset === 'display_tv_region' ||
        chart.dataset === 'day_of_week' ||
        chart.dataset === 'hour_of_day'
    ) {
        return [chart.dataset];
    }
    return [];
};

export const getGroupByFieldsKey = (fields: CustomGroupByField[]) => fields.join('__');

export const collectRequestedGroupByCombos = (slides: CustomReportSlideConfig[]) => {
    const chartSpecs: ChartSpec[] = [];
    const insightGroupByCombos: CustomGroupByField[][] = [];

    const collectInsightFields = (insight: InsightSpec) => {
        if (insight.kind === 'shareBreakdown') {
            if (insight.groupByFields?.length) {
                insightGroupByCombos.push(insight.groupByFields);
            }
            return;
        }

        if (insight.kind !== 'customRows') return;

        insight.rows.forEach(row => {
            if (
                (row.kind === 'topShare' || row.kind === 'othersShare') &&
                row.groupByFields?.length
            ) {
                insightGroupByCombos.push(row.groupByFields);
            }
        });
    };

    slides
        .filter(slide => slide.enabled !== false)
        .forEach(slide => {
            if (slide.layout === 'linearTwoChart') {
                chartSpecs.push(slide.left, slide.right);
            } else if (slide.layout === 'singleChart' || slide.layout === 'dspSplitChartInsights') {
                chartSpecs.push(slide.chart);
                if (slide.layout === 'dspSplitChartInsights') {
                    collectInsightFields(slide.insights);
                }
            }
        });

    const seen = new Set<string>();

    return [...chartSpecs.map(getChartGroupByFields), ...insightGroupByCombos]
        .filter(fields => fields.length > 1)
        .filter(fields => fields.every(field => MULTI_GROUP_BY_SUPPORTED_FIELDS.includes(field)))
        .filter(fields => {
            const key = getGroupByFieldsKey(fields);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
};

const dowName = (i: number, language: 'GB' | 'US') =>
    new Date(Date.UTC(2023, 0, 2 + i)).toLocaleDateString(language === 'GB' ? 'en-GB' : 'en-US', {
        weekday: 'long',
    });

export const buildDayRows = (raw: Record<string, any>, language: 'GB' | 'US') => {
    const normalizeDowName = (key: string) => {
        if (/^\d+$/.test(key)) {
            const rawIndex = parseInt(key, 10);
            const zeroBasedIndex =
                rawIndex >= 0 && rawIndex <= 6
                    ? rawIndex
                    : rawIndex >= 1 && rawIndex <= 7
                    ? rawIndex - 1
                    : null;
            if (zeroBasedIndex != null) return dowName(zeroBasedIndex, language);
        }

        const normalizedKey = key.trim().toLowerCase();
        const matched = DOW_ORDER.find(day => day.toLowerCase() === normalizedKey);
        if (matched) return matched;

        const matchedShort = DOW_ORDER.find(day =>
            day.toLowerCase().startsWith(normalizedKey.slice(0, 3)),
        );
        return matchedShort ?? key;
    };

    const sourceEntries = Object.keys(raw ?? {}).length
        ? Object.entries(raw as Record<string, any>)
        : ['0', '1', '2', '3', '4', '5', '6'].map(k => [k, raw?.[k] ?? {}]);

    const combined = sourceEntries.reduce<Record<string, SummaryBarRow>>((acc, [rawKey, value]) => {
        const name = normalizeDowName(String(rawKey));
        const current = acc[name] ?? {
            name,
            impacts_total: 0,
            impacts_expected: 0,
            plays_total: 0,
            plays_expected: 0,
        };

        acc[name] = {
            name,
            impacts_total: current.impacts_total + (value?.impacts ?? 0),
            impacts_expected: current.impacts_expected + (value?.expected_impacts_in_schedule ?? 0),
            plays_total: current.plays_total + (value?.sum_of_plays ?? 0),
            plays_expected: current.plays_expected + (value?.expected_plays_in_schedule ?? 0),
        };

        return acc;
    }, {});

    return DOW_ORDER.map(name => combined[name])
        .filter(Boolean)
        .filter(hasAnyMetrics);
};

export const buildHourRows = (raw: Record<string, any>) =>
    Array.from({ length: 24 }, (_, i) => String(i)).map(k => {
        const v = raw?.[k] ?? {};
        return {
            name: k.padStart(2, '0'),
            impacts_total: v?.impacts ?? 0,
            impacts_expected: v?.expected_impacts_in_schedule ?? 0,
            plays_total: v?.sum_of_plays ?? 0,
            plays_expected: v?.expected_plays_in_schedule ?? 0,
        };
    });

export const formatTime = (sec?: number) => {
    if (sec == null) return '0m';
    const totalM = Math.round(sec / 60);
    const h = Math.floor(totalM / 60);
    const m = totalM % 60;
    return h > 0 ? `${h.toLocaleString()} hrs ${m}m` : `${m}m`;
};

export const buildMetricCard = ({
    title,
    total,
    expected,
    formatter,
}: {
    title: string;
    total?: number;
    expected?: number;
    formatter: (v?: number) => string;
}): SummaryMetricCard => {
    const performance = expected && expected > 0 ? ((total ?? 0) / expected) * 100 : null;
    const varianceValue = (total ?? 0) - (expected ?? 0);

    return {
        title,
        performance,
        totalValue: total ?? 0,
        expectedValue: expected ?? 0,
        total: formatter(total),
        expected: formatter(expected),
        variance: `${varianceValue > 0 ? '+' : varianceValue < 0 ? '-' : ''}${formatter(
            Math.abs(varianceValue),
        )}`,
        varianceValue,
    };
};
